import React, {FC, useCallback, useEffect, useState, KeyboardEvent} from 'react'
import {useLoadedData} from '../../../hooks/useGetLoadedData'
import {SalesGroupByEnum, SalesPurchaseFilter} from '../../../../services/cellcast.v2.types'
import {
  useLazyGetSalesGraphNewUserCountByGroupQuery,
  useLazyGetSalesGraphNewUserGrowthByGroupQuery,
  useLazyGetSalesNewPurchaseQuery,
  useLazyGetSalesNewUserCountInRangeQuery,
  useLazyGetSalesNewUserCountQuery,
} from '../../../../services/cellcast.v2'
import {toast} from 'react-toastify'
import {KTSVG} from '../../../../_metronic/helpers'
import {Col, Row, Spinner} from 'react-bootstrap'
import moment from 'moment'
import ReactPaginate from 'react-paginate'
import NodataYet from '../../../Components/NodataYet'
import ReactDatePicker from 'react-datepicker'
import {ENV} from '../../../Utils/_helpers/EnvHelper'
import CountCard from './components/CountCard'
import GraphCard from './components/GraphCard'

const NewPurchasePage: FC = () => {
  const API_DOMAIN = ENV().REACT_APP_DOMAIN

  const today = new Date() // Current date
  const sevenDaysAgo = new Date(today.getTime() - 120 * 24 * 60 * 60 * 1000)

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([sevenDaysAgo, new Date()])
  const [startDate, endDate] = dateRange

  const {calculate, loadedData, setLoadedData} = useLoadedData()
  const [params, setParams] = useState<SalesPurchaseFilter>({
    page: 1,
    limit: 10,
    from: moment(startDate).format('YYYY-MM-DD'),
    to: moment(endDate).format('YYYY-MM-DD'),
    source: '',
  })

  const [triggerFetch, setTriggerFetch] = useState(false)

  // RTK Action
  const [fetch, {isLoading, data, isFetching}] = useLazyGetSalesNewPurchaseQuery()
  const [exportData, {isLoading: isExportLoading}] = useLazyGetSalesNewPurchaseQuery()
  const [fetchCountUser, {isLoading: isGetCountUserLoading, data: countUser}] =
    useLazyGetSalesNewUserCountQuery()
  const [
    fetchCountUserInRange,
    {isLoading: isGetGetCountUserInRangeLoading, data: countUserInRange},
  ] = useLazyGetSalesNewUserCountInRangeQuery()
  const [fetchGrowthIndustry, {isLoading: growthIndustryLoading, data: growthIndustry}] =
    useLazyGetSalesGraphNewUserGrowthByGroupQuery()
  const [fetchGrowthSales, {isLoading: growthSalesLoading, data: growthSales}] =
    useLazyGetSalesGraphNewUserGrowthByGroupQuery()
  const [fetchGrowthSource, {isLoading: growthSourceLoading, data: growthSource}] =
    useLazyGetSalesGraphNewUserGrowthByGroupQuery()

  const [fetchUserByIndustry, {isLoading: userIndustryLoading, data: userIndustry}] =
    useLazyGetSalesGraphNewUserCountByGroupQuery()
  const [fetchUserBySales, {isLoading: userSalesLoading, data: userSales}] =
    useLazyGetSalesGraphNewUserCountByGroupQuery()
  const [fetchUserBySource, {isLoading: userSourceLoading, data: userSource}] =
    useLazyGetSalesGraphNewUserCountByGroupQuery()

  useEffect(() => {
    fetch(params)
    fetchCountUser(null)
    fetchCountUserInRange(null)
    fetchGrowthIndustry({groupBy: SalesGroupByEnum.industry})
    fetchGrowthSales({groupBy: SalesGroupByEnum.sales})
    fetchGrowthSource({groupBy: SalesGroupByEnum.source})
    fetchUserByIndustry({groupBy: SalesGroupByEnum.industry})
    fetchUserBySales({groupBy: SalesGroupByEnum.sales})
    fetchUserBySource({groupBy: SalesGroupByEnum.source})
  }, [])

  useEffect(() => {
    if (triggerFetch && triggerFetch === true) {
      doFetch(params)
    }
    setTriggerFetch(false)
  }, [triggerFetch])

  useEffect(() => {
    if (data) {
      const loadedData = calculate(
        data && data.data.current ? data.data.current : 1,
        params.limit,
        data && data.data.total ? data.data.total : 0
      )
      setLoadedData(loadedData)
    }
  }, [isLoading, isFetching])

  const setDateParams = (dateRangeParams: [Date | null, Date | null]) => {
    let [startDate, endDate] = dateRangeParams

    if (endDate === null) {
      endDate = new Date()
    }

    setParams({
      ...params,
      from: moment(startDate).format('YYYY-MM-DD'),
      to: moment(endDate).format('YYYY-MM-DD'),
    })
    setDateRange(dateRangeParams)
    setTriggerFetch(true)
  }

  const doFetch = useCallback(
    (params) => {
      fetch(params)
        .then((res) => {
          if ('data' in res) {
          } else if ('error' in res) {
            toast.error(res.error)
          }
        })
        .catch((err) => {
          toast.error(err.error)
        })
    },
    [fetch]
  )

  const handleExports = useCallback(() => {
    exportData({
      ...params,
      from: moment(startDate).format('YYYY-MM-DD'),
      to: moment(endDate).format('YYYY-MM-DD'),
      mode: 'export',
    })
      .then((res) => {
        if ('data' in res) {
          const resData = res.data?.data as any
          window.open(`${API_DOMAIN}${resData?.downloadurl}`, '_self')
        } else if ('error' in res) {
          toast.error(res.error)
        }
      })
      .catch((err) => {
        toast.error(err.error)
      })
  }, [API_DOMAIN, endDate, exportData, params, startDate])

  const handleSearchEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      doFetch(params)
    }
  }

  const getSmsStatusText = (smsStatus: string) => {
    if (smsStatus === 'approved') {
      return <label className='fw-bold fs-7 text-success mt-3 text-nowrap'>Verified</label>
    }
    return <label className='fw-bold fs-7 text-danger mt-3 text-nowrap'>Unverified</label>
  }

  const getSeries = (graphData: any[]) => {
    return (
      graphData?.map((el) => {
        const tmp = {
          name: el.key,
          data: [],
        }

        Object.keys(el.data).forEach((key) => {
          if (el?.data[key] !== undefined) {
            const item = {x: key, y: el?.data[key].toFixed(2)} as never
            tmp.data.push(item)
          }
        })

        return tmp
      }) || []
    )
  }

  return (
    <>
      {/* card count summary */}
      <Row>
        <Col xs={12} sm={6} md={3}>
          <CountCard
            label='New User'
            value={countUser?.data || 0}
            isLoading={isGetCountUserLoading}
          />
        </Col>
        <Col xs={12} sm={6} md={3}>
          <CountCard
            label='User Purchase In Next 30 Day'
            value={countUserInRange?.data.totalUserPurchase30day || 0}
            isLoading={isGetGetCountUserInRangeLoading}
          />
        </Col>
        <Col xs={12} sm={6} md={3}>
          <CountCard
            label='User Purchase In Next 60 Day'
            value={countUserInRange?.data.totalUserPurchase60day || 0}
            isLoading={isGetGetCountUserInRangeLoading}
          />
        </Col>
        <Col xs={12} sm={6} md={3}>
          <CountCard
            label='User Purchase In Next 120 Day'
            value={countUserInRange?.data.totalUserPurchase120day || 0}
            isLoading={isGetGetCountUserInRangeLoading}
          />
        </Col>
      </Row>

      {/* user table */}
      <div className='mt-8'>
        <div className='card p-8'>
          <div className='card-header border-0 p-0 align-items-center mb-10'>
            <div className='d-flex flex-column w-100'>
              <h3 className='card-title align-items-start  flex-column'>
                <span className='card-label fw-bolder fs-2 mb-1'>New Sign Up Purchase</span>
                <span className='text-muted mt-1 fw-bold fs-7'>Ordered by most recent</span>
              </h3>
              <div className='d-flex justify-content-end'>
                <div className='d-flex align-items-start col-md-3 me-2'>
                  <KTSVG
                    path='/media/icons/duotune/general/gen021.svg'
                    className='svg-icon-1 position-absolute ms-6 mt-3'
                  />
                  <input
                    type='text'
                    value={params.source ? params.source : ''}
                    data-kt-user-table-filter='search'
                    className='form-control form-control-solid w-170px ps-14'
                    placeholder='input source & enter'
                    onChange={(e: any) => setParams({...params, source: e.target.value, page: 1})}
                    onKeyDown={handleSearchEnter}
                  />
                </div>
                <div className='col-md-3'>
                  <div className='d-flex'>
                    <ReactDatePicker
                      selectsRange={true}
                      dateFormat='MMM d, yyyy'
                      className=' border-0 form-control form-control-lg form-control-solid'
                      placeholderText='Pick data & time'
                      onChange={(update) => {
                        setDateParams(update)
                      }}
                      startDate={startDate}
                      endDate={endDate}
                      disabled={isLoading}
                    />
                    <span
                      className='input-group-text'
                      style={{backgroundColor: '#f5f8fa', borderRadius: 0, borderColor: '#f5f8fa'}}
                    >
                      <i className='bi bi-calendar'></i>
                    </span>
                  </div>
                </div>

                <div className='ms-2'>
                  <button
                    type='button'
                    onClick={() => handleExports()}
                    className='btn btn-light-primary'
                  >
                    {isExportLoading ? (
                      <Spinner />
                    ) : (
                      <KTSVG path='/media/icons/duotune/arrows/arr078.svg' className='svg-icon-2' />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          {isLoading ? (
            <div className='row flex-center h-200'>
              <Spinner />
            </div>
          ) : (
            <>
              {data?.data.items && data.data.items.length > 0 ? (
                <div className='card-body p-0 pt-1'>
                  <div className='table-responsive'>
                    <table className='table align-middle gs-0 gy-5 all-message-table'>
                      <thead>
                        <tr className='fw-400 text-muted'>
                          <th className='min-w-200px'>Name</th>
                          <th className='min-w-140px'>Email</th>
                          <th className='min-w-100px'>Source</th>
                          <th className='min-w-140px'>Account Owner</th>
                          <th className='min-w-100px'>Account Status </th>
                          <th className='min-w-120px'>Created At</th>
                          <th className='min-w-150px'>Tags</th>
                          <th className='min-w-100px'>Purchase in 30 Day</th>
                          <th className='min-w-100px'>Amount in 30 Day</th>
                          <th className='min-w-100px'>Purchase in 60 Day</th>
                          <th className='min-w-100px'>Amount in 60 Day</th>
                          <th className='min-w-100px'>Purchase in 90 Day</th>
                          <th className='min-w-100px'>Amount in 90 Day</th>
                          <th className='min-w-100px'>Purchase in 120 Day</th>
                          <th className='min-w-100px'>Amount in 120 Day</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isFetching ? (
                          <tr>
                            <td colSpan={13} className='text-center'>
                              <Spinner />
                            </td>
                          </tr>
                        ) : (
                          <>
                            {data?.data?.items?.map((item, i: number) => (
                              <React.Fragment key={i}>
                                <tr className='border-dashed border-2 border-bottom'>
                                  <td className='top-align'>{item?.name || '--'}</td>
                                  <td className='top-align'>{item?.email || '--'}</td>
                                  <td className='top-align'>{item?.source || '--'}</td>
                                  <td>
                                    <div>
                                      {item?.accountManagerData?.name && (
                                        <span className='text-muted fw-bold text-muted d-block fs-7 mb-2'>
                                          {item?.accountManagerData?.name &&
                                            item?.salesData?.name &&
                                            '-'}{' '}
                                          {item?.accountManagerData?.name}
                                        </span>
                                      )}
                                      {item?.salesData?.name && (
                                        <span className='text-muted fw-bold text-muted d-block fs-7'>
                                          {item?.accountManagerData?.name &&
                                            item?.salesData?.name &&
                                            '-'}{' '}
                                          {item?.salesData?.name}
                                        </span>
                                      )}

                                      {!item?.accountManagerData?.name && !item?.salesData?.name
                                        ? '-'
                                        : ''}
                                    </div>
                                  </td>
                                  <td className='top-align'>
                                    <div className='form-check form-check-sm form-check-custom form-check-solid'>
                                      {getSmsStatusText(item?.smsStatus)}
                                    </div>
                                  </td>
                                  <td className='top-align'>
                                    <span className='text-dark-400  p-0'>
                                      {moment(item.createdAt).format('DD-MM-YYYY HH:mm:ss')}
                                    </span>
                                  </td>
                                  <td
                                    data-bs-toggle='tooltip'
                                    data-bs-placement='top'
                                    title={item?.tags
                                      ?.filter((tag: any) => tag?.type === 'tag')
                                      ?.map((tag: any) => tag?.name)
                                      .join()}
                                  >
                                    <div className='d-flex gap-2 align-items-center flex-wrap '>
                                      {item?.tags?.map(
                                        (
                                          tag: {_id: string; name: string; color: string},
                                          i: number
                                        ) => (
                                          <div
                                            key={i}
                                            className='inbox-tags'
                                            style={{
                                              color: tag?.color ? 'white' : 'black',
                                              backgroundColor: tag?.color ? tag?.color : '',
                                            }}
                                          >
                                            {tag?.name}
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </td>
                                  <td className='top-align'>{item?.totalPurchase30day || '--'}</td>
                                  <td className='top-align'>{item?.totalAmount30day || '--'}</td>
                                  <td className='top-align'>{item?.totalPurchase60day || '--'}</td>
                                  <td className='top-align'>{item?.totalAmount60day || '--'}</td>
                                  <td className='top-align'>{item?.totalPurchase90day || '--'}</td>
                                  <td className='top-align'>{item?.totalAmount90day || '--'}</td>
                                  <td className='top-align'>{item?.totalPurchase120day || '--'}</td>
                                  <td className='top-align'>{item?.totalAmount120day || '--'}</td>
                                </tr>
                              </React.Fragment>
                            ))}
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className='abcd'>
                    <select
                      onChange={(e: any) => {
                        setParams({...params, limit: e.target.value})
                        setTriggerFetch(true)
                      }}
                      value={params.limit || 10}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                    {isFetching
                      ? '. . . . .'
                      : `Show ${loadedData.startLoad} - ${loadedData.endLoad} of ${
                          data.data.total ? data.data.total : 0
                        } Messages`}
                    <ReactPaginate
                      previousLabel='<'
                      nextLabel='>'
                      pageCount={data?.data?.totalPages}
                      onPageChange={(e: any) => {
                        setParams({...params, page: e.selected + 1})
                        setTriggerFetch(true)
                      }}
                      containerClassName={'pagination'}
                      previousLinkClassName={'pagination__link'}
                      nextLinkClassName={'pagination__link'}
                      disabledClassName={'pagination__link--disabled'}
                      activeClassName={'pagination__link--active'}
                    />
                  </div>
                </div>
              ) : (
                <NodataYet message='No purchase (yet!)' />
              )}
            </>
          )}
        </div>
      </div>

      {/* will show graph*/}
      <Row>
        <Col xs={12} sm={6} md={6}>
          <GraphCard
            label='Growth Per Industry (in AUD)'
            data={getSeries(growthIndustry?.data || [])}
            isLoading={growthIndustryLoading}
            format={'currency'}
          />
        </Col>
        <Col xs={12} sm={6} md={6}>
          <GraphCard
            label='Growth Per Source (in AUD)'
            data={getSeries(growthSource?.data || [])}
            isLoading={growthSourceLoading}
            format={'currency'}
          />
        </Col>
        <Col xs={12} sm={12} md={12}>
          <GraphCard
            label='Growth Per Sales (in AUD)'
            data={getSeries(growthSales?.data || [])}
            isLoading={growthSalesLoading}
            type={'bar'}
            format={'currency'}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} sm={6} md={6}>
          <GraphCard
            label='Sign Up Per Industry'
            data={getSeries(userIndustry?.data || [])}
            isLoading={userIndustryLoading}
          />
        </Col>
        <Col xs={12} sm={6} md={6}>
          <GraphCard
            label='Sign Up Per Source'
            data={getSeries(userSource?.data || [])}
            isLoading={userSourceLoading}
          />
        </Col>
        <Col xs={12} sm={12} md={12}>
          <GraphCard
            label='Sign Up Per Sales'
            data={getSeries(userSales?.data || [])}
            isLoading={userSalesLoading}
            type={'bar'}
          />
        </Col>
      </Row>
    </>
  )
}

export default NewPurchasePage
